
import React, { useState, useEffect, useRef } from "react";

interface TypingEffectProps {
  text: string | string[];
  className?: string;
  typingSpeed?: number;
  deletingSpeed?: number;
  delayBeforeDelete?: number;
  delayBeforeType?: number;
  infinite?: boolean;
  cursor?: boolean;
  onComplete?: () => void;
  colorClass?: string;
}

export default function TypingEffect({
  text,
  className,
  typingSpeed = 50,
  deletingSpeed = 30,
  delayBeforeDelete = 1500,
  delayBeforeType = 500,
  infinite = false,
  cursor = true,
  onComplete,
  colorClass = "text-terminal-pink",
}: TypingEffectProps) {
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const textArray = Array.isArray(text) ? text : [text];
  const currentText = textArray[textIndex];

  useEffect(() => {
    if (isComplete) return;

    const handleTyping = () => {
      if (!isDeleting) {
        // Typing
        if (displayedText.length < currentText.length) {
          const nextChar = currentText.charAt(displayedText.length);
          setDisplayedText((prev) => prev + nextChar);
          timeoutRef.current = setTimeout(
            handleTyping,
            typingSpeed + Math.random() * 50
          );
        } else if (infinite) {
          // Delay before deleting
          timeoutRef.current = setTimeout(() => {
            setIsDeleting(true);
            handleTyping();
          }, delayBeforeDelete);
        } else {
          // Single run completed
          setIsComplete(true);
          if (onComplete) onComplete();
        }
      } else {
        // Deleting
        if (displayedText.length > 0) {
          setDisplayedText((prev) => prev.slice(0, -1));
          timeoutRef.current = setTimeout(handleTyping, deletingSpeed);
        } else {
          // Move to next text
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % textArray.length);
          
          // Delay before typing the next word
          timeoutRef.current = setTimeout(handleTyping, delayBeforeType);
        }
      }
    };

    timeoutRef.current = setTimeout(handleTyping, delayBeforeType);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [
    displayedText,
    isDeleting,
    textIndex,
    currentText,
    textArray,
    typingSpeed,
    deletingSpeed,
    delayBeforeDelete,
    delayBeforeType,
    infinite,
    isComplete,
    onComplete,
  ]);

  return (
    <span className={className}>
      <span className={colorClass}>{displayedText}</span>
      {cursor && !isComplete && (
        <span className="inline-block w-2 h-4 ml-1 bg-terminal-pink animate-blink"></span>
      )}
    </span>
  );
}
